import * as Sentry from '@sentry/react';
import { apiService } from 'services';
import { orderBumpFormActions, userActions } from 'store/features';
import { IOrderBumpFormState } from 'store/features/orderBumpForm';
import { selectBumpsItems, selectOrderBumpForm, selectClearCacheContentIds } from 'store/selectors/orderBumpForm';
import { enOrderBumpStatus, enOrderBumpStatusBase } from 'types/enums';
import {
  IOrderBumpForm,
  IOrderBumpProduct,
  IOrderBumpFormItem,
  IOrderBumpList,
  IProductList,
  IGetValidMainProductsProps,
  IGetValidBumpsProps
} from 'types/interfaces';

import getOrderBumpErrorMessages from '../errors/orderBump/errorsMessages';
import store from '../store';
import { IOrderBumpProductsList, IOrderBumpPutPayload } from '../types/interfaces';
import { renderToastMessageApi } from '../utils/toastMessage';

class OrderBumpService {
  public getOrderBumps(term: string, currentPage = 1, perPage = 10, signal?: AbortSignal): Promise<IOrderBumpList> {
    try {
      const url = `/bump?${this.sanitizeSearchParams({ term, perPage, currentPage })}`;
      return apiService.get(url, undefined, undefined, undefined, signal);
    } catch (err) {
      Sentry.withScope(scope => {
        scope.setTag('error', 'error to get order bumps');
        scope.setTag('deepErrorCode', 'OBL_A');
        Sentry.captureException(err);
      });

      return Promise.resolve({
        bumps: [],
        total: 0,
        page: 0,
        lastPage: 0
      });
    }
  }

  public getValidMainProducts({
    pageParam: currentPage = 1,
    signal,
    term,
    ownerContentId
  }: IGetValidMainProductsProps): Promise<IProductList> {
    if (!currentPage) {
      return Promise.resolve({
        data: [],
        lastPage: 1,
        page: 1,
        total: 0
      });
    }

    const searchParams = { perPage: 10, currentPage, term, ownerContentId };
    const url = `/bump/valid?${this.sanitizeSearchParams(searchParams)}`;

    return apiService.get(url, undefined, 0, undefined, signal);
  }

  public getValidBumpsProducts(
    id: number | undefined,
    { pageParam: currentPage = 1, term, signal }: IGetValidBumpsProps
  ): Promise<IOrderBumpProductsList> {
    if (!id || !currentPage) {
      return Promise.resolve({
        data: [],
        lastPage: 1,
        page: 1,
        total: 0,
        bumpItemsLimit: 0
      });
    }

    const searchParams = { perPage: 10, currentPage, term };
    const url = `/bump/valid/${id}?${this.sanitizeSearchParams(searchParams)}`;

    return apiService.get(url, undefined, 0, undefined, signal);
  }

  public async getOrderBumpById(id: number): Promise<IOrderBumpForm<IOrderBumpFormItem>> {
    const orderbumps = await apiService.get(`/bump/${id}`, undefined, undefined, true);

    if (orderbumps?.items) {
      const items = this.removeDuplicatedOrderBump(orderbumps.items);
      orderbumps.items = items;
    }

    return orderbumps;
  }

  public async getOrderBump(id: number): Promise<IOrderBumpFormState> {
    const orderBump = await this.getOrderBumpById(id);
    const items = orderBump.items as IOrderBumpProduct[];

    const removedItemsContentIds = items
      .filter(item => item.status === enOrderBumpStatus.REMOVED)
      .map(item => item.contentId);

    const orderBumpsProducts = items.filter(item => item.status !== enOrderBumpStatus.REMOVED);
    const clearCacheContentIds = [orderBump.contentId] as number[];
    const orderBumpFormState: IOrderBumpFormState = {
      ...orderBump,
      removedItemsContentIds,
      orderBumpsProducts,
      clearCacheContentIds
    };

    store.dispatch(orderBumpFormActions.setState(orderBumpFormState));

    return orderBumpFormState;
  }

  public async updateStatusOrderBump(bumpId: number, isActive: boolean): Promise<void> {
    const status = isActive ? enOrderBumpStatusBase.ACTIVE : enOrderBumpStatusBase.INACTIVE;
    const response = await apiService.put(`/bump/${bumpId}/status`, { status });

    store.dispatch(userActions.setShowSurvey(response?.showSurvey));

    return response;
  }

  public async saveOrderBump(data: IOrderBumpForm<Omit<IOrderBumpFormItem, 'id'>>) {
    const payload = this.factoryPostPayload(data);
    const response = await apiService.post('/bump', payload);

    store.dispatch(userActions.setShowSurvey(response?.showSurvey));

    return response;
  }

  public async editOrderBump(data: IOrderBumpForm<IOrderBumpFormItem>) {
    const payload = this.factoryPutPayload(data);
    const validateOrderBump = this.validateOrderBumpBeforeSave(payload);

    if (!validateOrderBump.valid) {
      renderToastMessageApi(400, validateOrderBump.message);
      throw new Error(validateOrderBump.message);
    }

    await apiService.put('/bump', payload);
  }

  public removeAllBumps() {
    const bumpItems = selectBumpsItems(store.getState());
    const contentIdsToRemove = bumpItems?.map(item => item.contentId);

    if (!contentIdsToRemove?.length) {
      return;
    }

    store.dispatch(orderBumpFormActions.addRemovedItems(contentIdsToRemove));
    store.dispatch(orderBumpFormActions.setBumpItems([]));
  }

  public resetFormState() {
    store.dispatch(orderBumpFormActions.reset());
  }

  private factoryPostPayload(data: IOrderBumpForm<Omit<IOrderBumpFormItem, 'id'>>) {
    const payload = data;

    if (payload?.items) {
      payload.items = [...this.removeDuplicatedOrderBump(payload?.items)];
    }

    payload.clearCacheContentIds = selectClearCacheContentIds(store.getState());

    return payload;
  }

  private factoryPutPayload(data: IOrderBumpForm<IOrderBumpFormItem>) {
    const { items: itemsBeforeEdition, removedItemsContentIds } = selectOrderBumpForm(store.getState());

    const payload = {
      ...data,
      clearCacheContentIds: selectClearCacheContentIds(store.getState()),
      items: data.items?.map(item => ({
        id: item.id ?? 0,
        contentId: item.contentId,
        offerId: item.offerId,
        order: item.order,
        status: item.status ?? enOrderBumpStatus.ACTIVE,
        description: item?.description
      })) as IOrderBumpFormItem[]
    };

    itemsBeforeEdition?.map(item => {
      const isRemovedItem = removedItemsContentIds.includes(item.contentId);

      if (!isRemovedItem) {
        return;
      }

      payload.items?.push({
        id: item.id,
        contentId: item.contentId,
        order: item.order || 0,
        status: enOrderBumpStatus.REMOVED
      });
    });

    if (payload?.items) {
      payload.items = [...this.removeDuplicatedOrderBump(payload?.items)];
    }

    return payload as IOrderBumpPutPayload;
  }

  private validateOrderBumpBeforeSave(payload: IOrderBumpPutPayload) {
    const { mainProduct } = selectOrderBumpForm(store.getState());
    const invalidStatus = [enOrderBumpStatus.ERROR, enOrderBumpStatus.WARNING];

    let message = '';

    const hasInvalidItem =
      invalidStatus.includes(mainProduct?.status || enOrderBumpStatus.ACTIVE) ||
      payload.items.some(item => invalidStatus.includes(item?.status || enOrderBumpStatus.ACTIVE));

    if (hasInvalidItem) {
      message = getOrderBumpErrorMessages('invalid-items');
    }

    return {
      valid: !hasInvalidItem,
      message
    };
  }

  private removeDuplicatedOrderBump(bumpItems: Omit<IOrderBumpFormItem, 'id'>[]) {
    return bumpItems.filter((bumpItem, index, self) => {
      if (bumpItem.status === enOrderBumpStatus.REMOVED) {
        return true;
      }

      const foundIndex = self.findIndex(
        item => item && item?.contentId === bumpItem?.contentId && bumpItem.status === item.status
      );

      return foundIndex >= 0 && foundIndex === index;
    });
  }

  private sanitizeSearchParams(data: Record<string, any>) {
    const invalidValues = [undefined, null, ''];
    const clearedArray = Object.entries(data).filter(([, value]) => {
      return !invalidValues.includes(value);
    });

    return new URLSearchParams(Object.fromEntries(clearedArray)).toString();
  }
}

export { OrderBumpService };
export default new OrderBumpService();
